import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { IconBell } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Pagination } from "../../../../components/common/pagination";
import { PageContainer } from "../../../../components/common/page-container";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { useMyInbox } from "../../hooks/useMyInbox";
import { useInboxMutations } from "../../hooks/useInboxMutations";
import { useUnreadInboxCount } from "../../hooks/useUnreadInboxCount";
import { resolveNotificationLink } from "../../resolve-notification-link";
import { useMePartner } from "../../../../swr";
import type { NotificationDto, PaginationMeta } from "../../../../api";

const PAGE_SIZE = 15;

type Filter = "all" | "unread";

export function InboxPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<Filter>("all");
  const navigate = useNavigate();
  const location = useLocation();
  const { markRead, markAllRead } = useInboxMutations();
  const { data: unread } = useUnreadInboxCount();
  const { isSeller, isBuyer } = useMePartner();

  const { data, isLoading } = useMyInbox({
    page,
    pageSize: PAGE_SIZE,
    unreadOnly: filter === "unread",
  });

  const notifications = (data?.data ?? []) as NotificationDto[];
  const meta = data?.meta as PaginationMeta | undefined;
  const unreadCount = unread?.count ?? 0;

  const handleClick = async (n: NotificationDto) => {
    if (n.readAt === null) await markRead(n.id).catch(() => {});
    const target = resolveNotificationLink(n, { isSeller, isBuyer });
    if (!target) return;
    const current = `${location.pathname}${location.search}${location.hash}`;
    if (target !== current) navigate(target);
  };

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 max-md:gap-4">
        <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-3">
          <div className="flex flex-col gap-1">
            <Heading variant="h2" tone="dark">Notifications</Heading>
            <Text variant="bodySm" tone="muted">
              Order updates, document reviews, and announcements
            </Text>
          </div>
          {unreadCount > 0 && (
            <Button variant="secondary" size="sm" onClick={() => void markAllRead().catch(() => {})}>
              Mark all read
            </Button>
          )}
        </div>

        <SegmentedControl<Filter>
          value={filter}
          onChange={(v) => { setFilter(v); setPage(1); }}
          options={[
            { value: "all", label: "All" },
            { value: "unread", label: `Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}` },
          ]}
        />

        <div className="overflow-hidden rounded-2xl border border-borderDefault bg-bgWhite">
          {isLoading ? (
            <div className="px-6 py-8">
              <ShimmerLoader message="Fetching your notifications…" />
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={IconBell}
              title={filter === "unread" ? "No unread notifications" : "You're all caught up"}
              description={
                filter === "unread"
                  ? "New unread notifications will show up here."
                  : "Order updates, document reviews, and announcements will appear here."
              }
            />
          ) : (
            notifications.map((n) => (
              <button
                type="button"
                key={n.id}
                onClick={() => void handleClick(n)}
                className={clsx(
                  "flex w-full flex-col gap-1 border-b border-borderLight px-6 py-4 text-left last:border-b-0",
                  "transition-colors hover:bg-bgLight",
                  n.readAt === null && "bg-accentGreenTintBg",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <span
                      className={clsx(
                        "mt-1.5 size-2 shrink-0 rounded-full",
                        n.readAt === null ? "bg-accentGreenDark" : "bg-transparent",
                      )}
                    />
                    <Text variant="bodyBold" tone="dark">{n.title}</Text>
                  </div>
                  <Text variant="bodyXs" tone="muted" className="shrink-0">
                    {new Date(n.createdAt).toLocaleString("en-GB", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </Text>
                </div>
                <Text variant="bodySm" tone="body" className="pl-4">{n.body}</Text>
              </button>
            ))
          )}

          {meta && meta.pageCount > 1 && (
            <div className="flex justify-center border-t border-borderDefault px-6 py-4">
              <Pagination page={meta.currentPage} pageCount={meta.pageCount} onPageChange={setPage} />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

export default InboxPage;
